import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import VerifiedBadge from '../components/VerifiedBadge';
import {
  ArrowLeft,
  ShieldCheck,
  IdentificationCard,
  Buildings,
  CalendarBlank,
  Camera,
  X,
  PaperPlaneTilt,
  SpinnerGap,
  CheckCircle,
  Warning,
  Clock,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  pending: 'bg-amber-100 border-amber-400 text-amber-900',
  approved: 'bg-emerald-100 border-emerald-400 text-emerald-900',
  rejected: 'bg-red-100 border-red-400 text-red-900',
  withdrawn: 'bg-neutral-100 border-neutral-400 text-neutral-700',
};

function DocUploader({ urls, onChange, max = 3, testidPrefix }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if (urls.length + arr.length > max) {
      toast({ title: `Max ${max} files`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    const next = [...urls];
    const token = localStorage.getItem('token');
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const resp = await axios.post(`${API_URL}/api/verifications/upload-document`, fd, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        if (resp.data?.url) next.push(resp.data.url);
      } catch (e) {
        toast({ title: 'Upload failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      }
    }
    onChange(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {urls.map((u, i) => {
          const isPdf = u.toLowerCase().endsWith('.pdf');
          const href = u.startsWith('http') ? u : `${API_URL}${u}`;
          return (
            <div key={u} className="relative w-24 h-24 border-2 border-black rounded-lg overflow-hidden bg-white" data-testid={`${testidPrefix}-preview-${i}`}>
              {isPdf ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-700">
                  <Camera size={22} weight="bold" />
                  <span className="text-[10px] font-black mt-1">PDF</span>
                </div>
              ) : (
                <img src={href} alt="doc" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => onChange(urls.filter((_, idx) => idx !== i))}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full border-2 border-black flex items-center justify-center hover:bg-red-600"
                data-testid={`${testidPrefix}-remove-${i}`}
                aria-label="Remove document"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          );
        })}
        {urls.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={uploading}
            className="w-24 h-24 border-2 border-dashed border-neutral-400 rounded-lg flex flex-col items-center justify-center text-neutral-500 hover:text-neutral-900 hover:border-neutral-900 transition-all"
            data-testid={`${testidPrefix}-add-btn`}
          >
            {uploading ? <SpinnerGap size={22} className="animate-spin" /> : <><Camera size={22} weight="bold" /><span className="text-[10px] font-bold mt-1">Add</span></>}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testidPrefix}-file-input`}
      />
    </div>
  );
}

export default function ContractorVerification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // form
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insurancePolicy, setInsurancePolicy] = useState('');
  const [insuranceExpiry, setInsuranceExpiry] = useState('');
  const [notes, setNotes] = useState('');
  const [licenseDocs, setLicenseDocs] = useState([]);
  const [insuranceDocs, setInsuranceDocs] = useState([]);

  const userType = typeof window !== 'undefined' ? localStorage.getItem('userType') : null;
  useEffect(() => {
    if (userType !== 'contractor') navigate('/');
  }, [userType, navigate]);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/verifications/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubmission(r.data?.verification || null);
      setVerified(!!r.data?.verified);
    } catch (e) {
      toast({ title: 'Failed to load status', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  const handleSubmit = async () => {
    if (!licenseNumber.trim() || !licenseState.trim() || !licenseExpiry) {
      return toast({ title: 'License details required', variant: 'destructive' });
    }
    if (!insuranceProvider.trim() || !insurancePolicy.trim() || !insuranceExpiry) {
      return toast({ title: 'Insurance details required', variant: 'destructive' });
    }
    if (licenseDocs.length === 0) {
      return toast({ title: 'Upload at least one license document', variant: 'destructive' });
    }
    if (insuranceDocs.length === 0) {
      return toast({ title: 'Upload at least one insurance document', variant: 'destructive' });
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_URL}/api/verifications/submit`, {
        license_number: licenseNumber.trim(),
        license_state: licenseState.trim().toUpperCase(),
        license_expiry: licenseExpiry,
        insurance_provider: insuranceProvider.trim(),
        insurance_policy_number: insurancePolicy.trim(),
        insurance_expiry: insuranceExpiry,
        license_document_urls: licenseDocs,
        insurance_document_urls: insuranceDocs,
        notes: notes.trim() || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSubmission(r.data?.verification || null);
      toast({ title: 'Submitted for review', description: 'Our team typically responds within 1-2 business days.' });
    } catch (e) {
      toast({ title: 'Submission failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Withdraw your pending verification submission?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/verifications/withdraw`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchMine();
      toast({ title: 'Withdrawn' });
    } catch (e) {
      toast({ title: 'Failed to withdraw', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center text-neutral-600">
        <SpinnerGap size={28} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const status = submission?.status;
  const isPending = status === 'pending';
  const isApproved = status === 'approved' || verified;
  const canResubmit = !submission || ['rejected', 'withdrawn'].includes(status);

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="verification-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white py-6 border-b-4 border-black">
        <div className="max-w-3xl mx-auto px-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white" data-testid="back-btn">
            <ArrowLeft size={26} weight="bold" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
              <ShieldCheck size={28} weight="fill" className="text-emerald-400" />
              Verified Pro
            </h1>
            <p className="text-neutral-300 text-sm mt-1">Upload your license + insurance to earn the Verified Pro badge.</p>
          </div>
          {isApproved && <VerifiedBadge size="lg" />}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Current status */}
        {submission && (
          <div className={`border-2 rounded-xl p-4 ${STATUS_TONE[status] || 'bg-neutral-100 border-neutral-300'}`} data-testid="current-status-card">
            <div className="flex items-center gap-3 mb-1">
              {status === 'approved' && <CheckCircle size={22} weight="fill" />}
              {status === 'pending' && <Clock size={22} weight="bold" />}
              {status === 'rejected' && <Warning size={22} weight="fill" />}
              {status === 'withdrawn' && <Warning size={22} weight="bold" />}
              <p className="font-black uppercase tracking-wider text-sm">
                Status: {status}
              </p>
            </div>
            <p className="text-sm">
              {status === 'pending' && 'Your submission is under review. We usually respond within 1-2 business days.'}
              {status === 'approved' && `Approved on ${submission.reviewed_at ? new Date(submission.reviewed_at).toLocaleDateString() : ''}. Badge active until ${submission.verified_until ? new Date(submission.verified_until).toLocaleDateString() : ''}.`}
              {status === 'rejected' && `Reason: ${submission.reviewer_note || 'Not specified'}`}
              {status === 'withdrawn' && 'You withdrew this submission. You can re-submit anytime.'}
            </p>
            {isPending && (
              <button
                onClick={handleWithdraw}
                className="mt-3 h-9 px-3 text-xs font-bold rounded-lg border-2 border-amber-700 text-amber-900 hover:bg-amber-200"
                data-testid="withdraw-btn"
              >
                Withdraw submission
              </button>
            )}
          </div>
        )}

        {/* Form (hidden while pending or approved-current) */}
        {canResubmit && (
          <>
            <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] space-y-4">
              <h2 className="text-base font-black uppercase tracking-wider text-neutral-700 border-b-2 border-neutral-200 pb-2 flex items-center gap-2">
                <IdentificationCard size={18} weight="fill" /> Contractor License
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="font-bold mb-1.5 block">License number</Label>
                  <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} className="border-2 border-black rounded-lg" placeholder="e.g. CGC1518478" data-testid="license-number" />
                </div>
                <div>
                  <Label className="font-bold mb-1.5 block">Issuing state</Label>
                  <Input value={licenseState} onChange={(e) => setLicenseState(e.target.value.toUpperCase())} maxLength={4} className="border-2 border-black rounded-lg uppercase" placeholder="TX" data-testid="license-state" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-bold mb-1.5 block flex items-center gap-1"><CalendarBlank size={14} weight="bold" /> Expiry date</Label>
                  <Input type="date" value={licenseExpiry} onChange={(e) => setLicenseExpiry(e.target.value)} className="border-2 border-black rounded-lg" data-testid="license-expiry" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-bold mb-1.5 block">License document(s)</Label>
                  <DocUploader urls={licenseDocs} onChange={setLicenseDocs} max={3} testidPrefix="license-doc" />
                  <p className="text-[11px] text-neutral-500 mt-2">Photos or PDFs · up to 8 MB each · max 3 files</p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] space-y-4">
              <h2 className="text-base font-black uppercase tracking-wider text-neutral-700 border-b-2 border-neutral-200 pb-2 flex items-center gap-2">
                <Buildings size={18} weight="fill" /> General Liability Insurance
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="font-bold mb-1.5 block">Insurance provider</Label>
                  <Input value={insuranceProvider} onChange={(e) => setInsuranceProvider(e.target.value)} className="border-2 border-black rounded-lg" placeholder="e.g. Hiscox" data-testid="insurance-provider" />
                </div>
                <div>
                  <Label className="font-bold mb-1.5 block">Policy #</Label>
                  <Input value={insurancePolicy} onChange={(e) => setInsurancePolicy(e.target.value)} className="border-2 border-black rounded-lg" placeholder="e.g. UDC-1234567" data-testid="insurance-policy" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-bold mb-1.5 block flex items-center gap-1"><CalendarBlank size={14} weight="bold" /> Expiry date</Label>
                  <Input type="date" value={insuranceExpiry} onChange={(e) => setInsuranceExpiry(e.target.value)} className="border-2 border-black rounded-lg" data-testid="insurance-expiry" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-bold mb-1.5 block">Certificate of insurance / proof</Label>
                  <DocUploader urls={insuranceDocs} onChange={setInsuranceDocs} max={3} testidPrefix="insurance-doc" />
                  <p className="text-[11px] text-neutral-500 mt-2">Photos or PDFs · up to 8 MB each · max 3 files</p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]">
              <Label className="font-bold mb-2 block">Anything we should know? (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Additional context, certifications, references…"
                className="border-2 border-black rounded-lg"
                data-testid="notes"
              />
            </div>

            <div className="flex gap-3 sticky bottom-0 bg-[#F0F0F2] py-3 -mx-4 px-4 border-t-2 border-black sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:border-0">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 h-12 flex items-center justify-center gap-2 bg-emerald-500 text-black font-black rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                data-testid="submit-verification-btn"
              >
                {submitting ? <SpinnerGap size={20} className="animate-spin" /> : <><PaperPlaneTilt size={18} weight="bold" /> Submit for review</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
